import React, { useCallback, useState, useEffect, useRef, useContext, useImperativeHandle } from "react";
import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap"
import CollapsablePanel from "../../common/collapsable-panel/collapsable-panel";
import PositionsIndividuals, { PositionListRef } from "./positions-individuals/positions-individuals";
import { IOrg, isCategory } from "../../../types/org";
import RolesIndividuals, { RoleListRef } from "./roles-individuals/roles-individuals";
import { AddIcon, CancelIcon, CheckIcon, DeleteIcon, MoveIcon } from "../../common/icons";
import { IPosition } from "../../../types/position";
import PositionDetailsInfo from "../../position/position";
import RoleDetailsInfo from "../../role/role";
import PositionRoleCreateModal from "../position-role-create/position-role-create";
import { ApolloCache, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import useCheckGroup from "../../../hook/useCheckGroup";
import { EXPIRE_POSITION_ROLE, GET_ORG_POSITIONS, GET_ORG_ROLES, MOVE_POSITION_ROLE } from "../../../graphql/position-gql";
import { notifySuccess } from "../../../services/notification";
import confirm from "../../common/confirm";
import { messages } from "../../../i18n/message";
import { LOCALES } from "../../../i18n/locales";
import MovePositionDialog from "./move-position-role";
import SpinnerOverlay from "../../common/spinner/spinner";
import { IS_SCOPE } from "../../../graphql/org-gql";
import { CategoryType } from "../../../types/constants";
import { IIndividual } from "../../../types/individual";
import Individual from "../../individual/individual";
import useAuth from "../../../hook/useAuth";
import useScrollIntoView from "../../../hook/useScrollIntoView";
import { OrgReducerContext } from "../org-context";
import Button from "../../common/button";

type PositionsRolesProps = {
    org: IOrg;
  };

const PositionsRoles :React.FC<PositionsRolesProps> = ({ org })=>{
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
    const [isRole, setIsRole] = useState<boolean>(false)
    const [cloned,setCloned] = useState<boolean>(false);
    const [selectedPosition,setSelectedPosition] = useState<IPosition>()
    const [selectedRole, setSelectedRole] = useState<IPosition>()
    const [selectedIndividual,setSelectedIndividual] = useState<IIndividual>()
    const [selectedIndividualPositionRole,setSelectedIndividualPositionRole] = useState<IPosition>()
    const {checkGroup: positionCheckGroup} = useCheckGroup<number>()
    const {checkGroup: roleCheckGroup} = useCheckGroup<number>()
    const [createPositionOpen, setCreatePositionOpen] = useState<boolean>(false)
    const [movePositionOpen, setMovePositionOpen] = useState<boolean>(false)
    const [state, dispatch] = useContext(OrgReducerContext);
    const { data:scopeData}  = useQuery(IS_SCOPE,{variables:{orgId:org.id,isLowerLevel:false}});
    const [expirePositonRole,{loading:expireLoading}] = useMutation(EXPIRE_POSITION_ROLE)
    const [movePositionRole,{loading:moveLoading}] = useMutation(MOVE_POSITION_ROLE)
    const toggleCreatePosition = useCallback(() => {setCreatePositionOpen(!createPositionOpen)}, [createPositionOpen])
    const toggleMovePosition = useCallback(() => {setMovePositionOpen(!movePositionOpen)}, [movePositionOpen])
    const toggleDropdownOpen = useCallback(() => {setDropdownOpen(!dropdownOpen)}, [dropdownOpen])
    const move = org.details?.category?.type === CategoryType.ORGANIZATION
    const { isFrenchTranslator } = useAuth();
    const selectedPositionRef = useRef<HTMLDivElement>(null);
    const selectedRoleRef = useRef<HTMLDivElement>(null);
    const selectedIndividualRef = useRef<HTMLDivElement>(null);
    const positionListRef = useRef<PositionListRef>(null);
    const roleListRef = useRef<RoleListRef>(null);
    useScrollIntoView(selectedPositionRef, [
        selectedPosition
    ]);
    useScrollIntoView(selectedRoleRef, [
        selectedRole
    ]);
    useScrollIntoView(selectedIndividualRef, [
        selectedIndividual,selectedIndividualPositionRole
    ]);

    useEffect(() => {
        //console.log("org:",org)
        setCloned(!!org.details?.shareLinkId)
        setSelectedPosition(undefined)
        setSelectedRole(undefined)
        setSelectedIndividual(undefined)
    },[org])

    const onSelectPositionHandle=(position:IPosition)=>{
        setSelectedRole(undefined)
        setSelectedIndividual(undefined)
        setSelectedPosition(position);
        dispatch({type: 'set-last-selected-item', payload: position});
    }

    const onSelectIndividualHandle=(individual:IIndividual,positionRole:IPosition)=>{
        setSelectedRole(undefined)
        setSelectedPosition(undefined)
        setSelectedIndividual(individual)
        setSelectedIndividualPositionRole(positionRole);
        dispatch({type: 'set-last-selected-item', payload: lastSelectedIndividual(individual, positionRole)});
    }

    const lastSelectedIndividual=(individual:IIndividual, positionRole:IPosition)=>{
        return `select-individual-position-role-${individual.individualId}-${positionRole.positionId}`;
    }

    const onSelectRoleHandle=(role:IPosition)=>{
        setSelectedPosition(undefined)
        setSelectedIndividual(undefined)
        setSelectedRole(role);
        dispatch({type: 'set-last-selected-item', payload: role});
    }

    const onCreatePositionHandle = ()=>{
        setIsRole(false)
        toggleCreatePosition();
    }
    const onCreateRoleHandle = ()=>{
        setIsRole(true)
        toggleCreatePosition();
    }
    const expireRefetchQueries = () => {
        return [{query: GET_ORG_ROLES, variables: {  orgId: org.id||0 } },{query: GET_ORG_POSITIONS, variables: {  orgId: org.id||0 } }]
    }

    const onExpirePositionOrRoleHandle = () => {
        expirePositionOrRoleConfirm().then(() => {
            expirePositionOrRoleExecution()
        })
    }

    const expirePositionOrRoleConfirm = () => {
        return confirm({
            title: 'Expire selected Position(s) or Role(s)',
            content: messages[LOCALES.ENGLISH].INFO['4.1'],
            okText: <><CheckIcon/> Expire</>,
            okBtnColor: 'danger',
            titleCenter: true,
            bodyCenter: true
        })
    }

    const expirePositionOrRoleExecution=()=>{
        const positionIds = positionCheckGroup.getCheckedValues().concat(roleCheckGroup.getCheckedValues())
        expirePositonRole({
            variables:{orgId:org?.id||0,positionIds:positionIds},
            onCompleted:(data)=>{
                if (data?.expirePositionOrRole && !data.expirePositionOrRole.success) {
                    expireValidationConfirm(data.expirePositionOrRole.message)
                }else{
                    setSelectedPosition(undefined);
                    notifySuccess();
                }
            },
            awaitRefetchQueries:true,
            refetchQueries:expireRefetchQueries()
        })
    }

    const expireValidationConfirm = (message:string) => {
        return confirm({
            title: 'Warning!',
            content: message,
            okText: <><CancelIcon/> Close</>,
            okBtnColor: 'primary',
            titleCenter: true,
            bodyCenter: true,
            showCancel:false
        })
    }


    const onOpenMoveDialogHandle = () => {
        toggleMovePosition()
    }

    const onMoveHandle = async (destOrg: IOrg|undefined) => {
        if(!destOrg) return;
        try {
            const positionIds = positionCheckGroup.getCheckedValues().concat(roleCheckGroup.getCheckedValues());
            const validationResult = await movePositionRoleValidation(destOrg,positionIds)
            if(validationResult.data?.movePostionOrRole && !validationResult.data.movePostionOrRole.success){
                movePositionRoleValidationConfirm(validationResult.data.movePostionOrRole.message)
            }else{
                const automationResult = await movePositionRoleAutomation(destOrg,positionIds)
                if(automationResult.data?.movePostionOrRole?.message){
                    movePositionRoleAutomationConfirm(automationResult.data.movePostionOrRole.message).then(()=>{
                        movePositionRoleExecution(destOrg,positionIds)
                    })
                }else{
                    movePositionRoleExecution(destOrg,positionIds)
                }
            }
        } catch (error) {
            console.error("Error during movePositionRole:", error);
            // Handle error appropriately
        }
    };

    const movePositionRoleValidation = async (destOrg: IOrg,positionIds:number[]) => {
        return await movePositionRole({
            variables: {
                "action": "MOVE_VALIDATION",
                "sourceOrgId": org.id,
                "destOrgId": destOrg.id,
                "positionIds": positionIds,
            },
        });
    }

    const movePositionRoleValidationConfirm = (message:string) => {
        return confirm({
            title: 'Warning!',
            content: message,
            okText: <><CancelIcon/> Close</>,
            okBtnColor: 'primary',
            titleCenter: true,
            bodyCenter: true,
            showCancel:false
        })
    }

    const movePositionRoleAutomation = async (destOrg: IOrg,positionIds:number[]) => {
        return await movePositionRole({
            variables: {
                "action": "MOVE_AUTOMATION",
                "sourceOrgId": org.id,
                "destOrgId": destOrg.id,
                "positionIds": positionIds,
            },
        });
    }

    const movePositionRoleAutomationConfirm = (message:string) => {
        return confirm({
            title: 'Warning!',
            content: message,
            okText: <><CheckIcon/> OK</>,
            okBtnColor: 'primary',
            titleCenter: true,
            bodyCenter: true,
        })
    }

    const movePositionRoleExecution = (destOrg: IOrg,positionIds:number[]) => {
        movePositionRole({
            variables: {
                "action": "MOVE",
                "sourceOrgId": org.id,
                "destOrgId": destOrg.id,
                "positionIds": positionIds,
            },
            refetchQueries: moveRefetchQueries(destOrg),
            update(cache,{data}){updateCache(cache,data,destOrg.id,org.id,positionIds)},
            onCompleted:(data)=>{
                data&&notifySuccess()
                setSelectedPosition(undefined)
                setSelectedRole(undefined)
            }
        });
    }

    const moveRefetchQueries = (destOrg:IOrg) =>{
        let queries:any =[];
        if(org.sharedToOrgIds){
            org.sharedToOrgIds.split(',').forEach(orgId=>{
                queries = [...queries,{query: GET_ORG_ROLES, variables: {  orgId} },{query: GET_ORG_POSITIONS, variables: {  orgId} }]
            })
        }
        if(destOrg.sharedToOrgIds){
            destOrg.sharedToOrgIds.split(',').forEach(orgId=>{
                queries = [...queries,{query: GET_ORG_ROLES, variables: {  orgId} },{query: GET_ORG_POSITIONS, variables: {  orgId} }]
            })
        }

        return queries;
    }

    const updateCache = (cache : ApolloCache <any>, result: any,destOrgId:number,sourceOrgId:number,positionIds:number[]) => {

        const selectedRoles = roleListRef.current?.getRoleList();
        if(selectedRoles&&selectedRoles.length>0){
            const cachedFromRoles = cache.readQuery<{org:IOrg}>({
                query:GET_ORG_ROLES,
                variables:{orgId: sourceOrgId}
            })?.org.roles;
            const updatedFromRoles = cachedFromRoles?.filter(itemA=>!selectedRoles.some(itemB => itemB.positionId === itemA.positionId))      
            cache.writeQuery({
                query:GET_ORG_ROLES,
                variables:{orgId: sourceOrgId},
                data:{
                    org:{
                        roles:updatedFromRoles
                    }
                }
            })
            const cachedToRoles = cache.readQuery<{org:IOrg}>({
                query:GET_ORG_ROLES,
                variables:{orgId: destOrgId}
            })?.org.roles;
            const updatedToRoles = cachedToRoles?.filter(itemA=>!selectedRoles.some(itemB => itemB.positionId === itemA.positionId))      
            cache.writeQuery({
                query:GET_ORG_ROLES,
                variables:{orgId: destOrgId},
                data:{
                    org:{
                        roles:updatedToRoles
                    }
                }
            })
        }

        const selectedPositions = positionListRef.current?.getPositonList();
        if(selectedPositions&&selectedPositions.length>0){
            const cachedFromPositions = cache.readQuery<{org:IOrg}>({
                query:GET_ORG_POSITIONS,
                variables:{orgId: sourceOrgId}
            })?.org.positions;
            const updatedFromPositions = cachedFromPositions?.filter(itemA=>!selectedPositions.some(itemB => itemB.positionId === itemA.positionId))      
            cache.writeQuery({
                query:GET_ORG_POSITIONS,
                variables:{orgId: sourceOrgId},
                data:{
                    org:{
                        positions:updatedFromPositions
                    }
                }
            })
            const cachedToPositions = cache.readQuery<{org:IOrg}>({
                query:GET_ORG_POSITIONS,
                variables:{orgId: destOrgId}
            })?.org.positions;
            const updatedToPositions = cachedToPositions?.filter(itemA=>!selectedPositions.some(itemB => itemB.positionId === itemA.positionId))      
            cache.writeQuery({
                query:GET_ORG_POSITIONS,
                variables:{orgId: destOrgId},
                data:{
                    org:{
                        positions:updatedToPositions
                    }
                }
            })
        }

        

    }

    const onHandleRemoveMember = (memberRemoved: boolean) =>{
        if(memberRemoved){
            setSelectedIndividual(undefined)
           // setSelectedPosition(undefined)
            setSelectedRole(undefined)
        }
    }

    return(
        <React.Fragment>
            {(expireLoading||moveLoading) && <SpinnerOverlay />}
            {<PositionRoleCreateModal isOpen={createPositionOpen} toggle={toggleCreatePosition} isRole={isRole} org={org}></PositionRoleCreateModal>}
            {<MovePositionDialog isOpen={movePositionOpen} toggle={toggleMovePosition} onMoveHandle={onMoveHandle}></MovePositionDialog>}
            <div>
               {org.details?.category?.type === CategoryType.ORGANIZATION && !isFrenchTranslator && (
                    <ButtonDropdown isOpen={dropdownOpen} toggle={toggleDropdownOpen} style={{ marginRight: '15px' }} disabled={cloned}>
                    <DropdownToggle caret color="primary" disabled={cloned}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleDropdownOpen();
                            }
                        }}>
                        <AddIcon/>
                        &nbsp;<span role="region" aria-label="Add">Add</span>
                    </DropdownToggle>
                    <DropdownMenu style={{ overflowY: 'auto' }}>
                        <DropdownItem disabled={!scopeData?.isScope} 
                            onKeyDown={(e) => {
                                if ((e.key === 'Enter' || e.key === ' ') && scopeData?.isScope) {
                                e.preventDefault();
                                onCreatePositionHandle();
                                }
                            }}
                            onClick={onCreatePositionHandle}>
                            Position
                        </DropdownItem>
                        <DropdownItem disabled={!scopeData?.isScope} 
                            onKeyDown={(e) => {
                                if ((e.key === 'Enter' || e.key === ' ') && scopeData?.isScope) {
                                e.preventDefault();
                                onCreateRoleHandle();
                                }
                            }}
                            onClick={onCreateRoleHandle}>
                            Role
                        </DropdownItem>
                    </DropdownMenu>
                </ButtonDropdown>
                )}
                {org.details?.category?.type === CategoryType.ORGANIZATION && !isFrenchTranslator && <Button color="danger" style={{ marginRight: '15px' }}
                    disabled={(positionCheckGroup.isNoneChecked() && roleCheckGroup.isNoneChecked())||cloned}
                    onClick={onExpirePositionOrRoleHandle}>
                    <DeleteIcon />
                    &nbsp;<span role="region" aria-label="Expire">Expire</span>
                </Button>
               }
                {org.details?.category?.type === CategoryType.ORGANIZATION && !isFrenchTranslator && <Button color="primary" style={{ marginRight: '15px' }}
                    disabled={(positionCheckGroup.isNoneChecked() && roleCheckGroup.isNoneChecked())||cloned}
                    onClick={onOpenMoveDialogHandle}>
                    <MoveIcon />
                    &nbsp;<span role="region" aria-label="Move">Move</span>
                </Button>
                }                
            </div>

            {org.details?.category?.type !== CategoryType.INFORMALGROUP && 
            <CollapsablePanel headTitle="Positions & Individuals" initCollapse={false}>
                <PositionsIndividuals ref={positionListRef} org={org} positionSelected={onSelectPositionHandle} individualSelected = {onSelectIndividualHandle} checkGroup={positionCheckGroup} lastSelectedIndividual={lastSelectedIndividual}/>
            </CollapsablePanel>
            }
            <CollapsablePanel headTitle="Roles & Individuals" css_classes="mt-3" initCollapse={false}>
                <RolesIndividuals ref={roleListRef} org={org} onRoleSelect={onSelectRoleHandle} individualSelected = {onSelectIndividualHandle} checkGroup={roleCheckGroup} onMemberRemove={onHandleRemoveMember} lastSelectedIndividual={lastSelectedIndividual}/>
            </CollapsablePanel>
            
            <div ref={selectedPositionRef}>
            {
                selectedPosition && (
                    <>
                  
                        <PositionDetailsInfo  position={selectedPosition} updateSelectedPosition={onSelectPositionHandle} onExpirePosition={()=>setSelectedPosition(undefined)}/>
                    </>
                )
            }
            </div>
            <div ref={selectedRoleRef}>
            {
                selectedRole &&(
                    <>
                       
                        <RoleDetailsInfo role = {selectedRole} updateSelectedRole = {onSelectRoleHandle}/>
                    </>
                )
            }
            </div>
            <div ref={selectedIndividualRef}>
            {
                selectedIndividual &&(
                    <>
                      
                        <Individual org={org} position={selectedIndividualPositionRole} individual={selectedIndividual}/>
                    </>
                )
            }
            </div>
        </React.Fragment>
    )
}
export default PositionsRoles;
